/**
 * Guided creation flow: the New Campaigns module (Campaigns + Templates tabs).
 *
 * Hangs off the New Campaigns stop. That module is the REDESIGN of Campaigns and
 * Templates — the same data, the same create flows, a new shell — so this takes its
 * two tabs ONE AT A TIME: ring the tab, explain what it is for, and offer a button
 * that starts the work on it. Campaigns first (build and send one), then Templates
 * (write the kind of wording it sends).
 *
 * ┌─ WHY EACH TAB IS A GATE, NOT JUST A STEP ON THE WAY PAST ──────────────┐
 * │ The two tab steps are the only ones whose Next PERFORMS the work rather   │
 * │ than turning a page, so they carry their own `nextLabel` ("Try it: build  │
 * │ a campaign" / "Try it: write a template") instead of a bare "Next" —      │
 * │ the same offer the tour popover makes to get here in the first place. The │
 * │ user sees where they are and chooses to start, per tab.                   │
 * │                                                                          │
 * │ `campaigns-tab` MUST set `forceHighlight`. `beforeStart` lands on that    │
 * │ tab, so its `satisfied()` is already true on entry and `TaskEngine`'s     │
 * │ fast path would skip the ring entirely — the first thing the flow is      │
 * │ meant to show would be the one thing never drawn.                          │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ WHY CAMPAIGNS COMES FIRST, GIVEN IT DEPENDS ON A TEMPLATE ────────────┐
 * │ A campaign can only send an APPROVED template, and the one this flow      │
 * │ writes is submitted as PENDING — so the campaign could never have used it │
 * │ even when the template was built first. It picks from the templates the   │
 * │ account already had approved, which is exactly what a real first campaign │
 * │ does. Ordering the tabs the way the page presents them therefore costs    │
 * │ nothing, and the dependency is still taught: the picker step says where   │
 * │ that list comes from, and the Templates tab then shows it.                │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ WHY THE TABS ARE STEPS OF A FLOW AND NOT STOPS OF THE TOUR ───────────┐
 * │ The tour walks the SIDEBAR: every stop is a menu row, resolved when the   │
 * │ run is assembled, and `order: 'dom'` sorts them by their position in that  │
 * │ menu. The Campaigns/Templates tabs are not in the sidebar — they live      │
 * │ INSIDE the module's own page, so they do not exist in the DOM until        │
 * │ something has navigated there, and a tour step whose anchor cannot resolve │
 * │ at assembly time is dropped. A task is the mechanism that can: it          │
 * │ navigates, waits, and re-resolves per step, which is exactly what "open    │
 * │ the module and explain its tabs" needs — one tab per gate.                 │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ WHAT EACH FORM ACTUALLY DEMANDS ──────────────────────────────────────┐
 * │ TEMPLATE (`NewTemplateCreationDrawer.isFormValid`): name, category,      │
 * │ language and body. Its initial state already sets `category: "MARKETING"` │
 * │ and `language: "en_US"`, so of the four only NAME and BODY start empty —  │
 * │ and the other two are Radix `Select`s, which is why this flow rings and   │
 * │ explains them rather than driving them.                                   │
 * │                                                                          │
 * │ 🔴 THE FLOW ALSO MAKES TWO OPTIONAL FIELDS REQUIRED, deliberately. It     │
 * │ switches the TEXT HEADER on and adds a QUICK REPLY BUTTON, because a      │
 * │ template with neither shows the user two thirds of a WhatsApp message.    │
 * │ Both then have to be filled: `isFormValid` refuses an enabled header with │
 * │ no text, and `validateButtonItems` refuses a button row with no label.    │
 * │ Every step that switches one on fills it in the step immediately after —  │
 * │ leaving either half-done is what greys out Create Template with no        │
 * │ explanation on screen.                                                    │
 * │                                                                          │
 * │ CAMPAIGN (`isCampaignSubmitDisabled` in `NewCampaignCreationDrawer`): a   │
 * │ NAME, a TEMPLATE, and at least one recipient selection. All three are     │
 * │ filled in here, so the form is genuinely submittable by the last step,    │
 * │ and every value stays editable in place.                                  │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ THE CAMPAIGN SEGMENT WALKS THE WHOLE FORM, IN THE FORM'S OWN ORDER ───┐
 * │ Name → template → its variables → link tracking →                        │
 * │ preview → audience source → audience → cost → advanced settings (retries, │
 * │ opted-out, note) → forecast → timing → the send menu → send. That is the  │
 * │ order the drawer lays the controls out in, top to bottom and then down the │
 * │ right-hand column, so the ring never jumps back up a screen the user has   │
 * │ already scrolled past.                                                     │
 * │                                                                            │
 * │ 🔴 THE STEPS THAT ONLY EXPLAIN ARE STILL STEPS. Link tracking, the preview, │
 * │ the cost row and the two advanced toggles have                             │
 * │ nothing to fill in and no `satisfied()` — the drawer's own defaults are    │
 * │ already the right answer for a first campaign. Leaving them out was what   │
 * │ made the flow "skip things": the form has fourteen decision points and the │
 * │ walkthrough used to name five of them, so a visitor reached the send       │
 * │ button having never been told what the other nine were for.                │
 * │                                                                            │
 * │ A step with NO `satisfied()` can never be recorded as skipped (the engine  │
 * │ treats an absent predicate as always-true), which is exactly right for one │
 * │ that cannot be failed — see the note on `campaign-send-mode`.              │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ THE TEMPLATES SEGMENT WALKS THE WHOLE COMPOSER TOO ───────────────────┐
 * │ AI-or-by-hand → name → category → layout → language → header → header     │
 * │ text → body → variable samples → footer → buttons → button text →         │
 * │ preview → submit. Same rule as the campaign segment: the drawer's own      │
 * │ order, top to bottom and then the right-hand column.                       │
 * │                                                                            │
 * │ 🔴 THE FIRST STEP OF THIS SEGMENT USED TO BE INVISIBLE. `template-skip-ai` │
 * │ was written when the composer opened ON its AI panel and had to hide it;   │
 * │ `showAiPanel` is initialised FALSE now, so its target (a Hide button) was  │
 * │ not on screen and its `satisfied()` — "is the form visible?" — was true    │
 * │ the moment the drawer opened. As an `auto` step that is the engine's        │
 * │ already-satisfied fast path, so it was skipped on EVERY run. Its           │
 * │ replacement rings the button that is actually there and is MANUAL, which   │
 * │ the fast path never short-cuts.                                            │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ WHAT GETS FILLED IN, AND WHY THOSE FIELDS ────────────────────────────┐
 * │ The campaign NAME, every TEMPLATE VARIABLE, and the internal NOTE. The    │
 * │ first two because the form cannot be sent without them and a blank        │
 * │ {{1}} would go out as a hole in the message; the note because the step    │
 * │ explaining where it lives is worthless against an empty box. Everything   │
 * │ else the flow touches is a CHOICE it makes with a click (the template,    │
 * │ the audience source and row, the forecast toggle), not a value it types.  │
 * │                                                                          │
 * │ Deliberately NOT touched: link tracking and the opted-out toggle, both of │
 * │ which change what actually happens on the send, and the retry schedule,   │
 * │ which the drawer already defaults to a sane 24 hours.                     │
 * │                                                                          │
 * │ On the TEMPLATE side: the name, the header line, the body, both variable  │
 * │ SAMPLES and the button label. Not the footer — the composer already put   │
 * │ "Reply STOP to unsubscribe" there and `fillField` leaves a non-empty      │
 * │ field alone, so declaring a fill for it would be a no-op that reads as    │
 * │ though the flow typed it.                                                 │
 * └────────────────────────────────────────────────────────────────────────┘
 */

import { resolveAnchor, sidebarAnchor, type AnchorSpec } from '../engine/anchors';
import { closeFlowChrome } from './closeChrome';
import {
  chooseFirstOption,
  clickWhenReady,
  ensureChecked,
  findVisible,
  isChecked,
  isChosen,
  isFilled,
  waitUntil,
} from './formFill';
import type { TaskFlow } from './types';

/** The module's two routes — one per tab. */
const CAMPAIGNS_PATH = '/home/new-campaigns';
const TEMPLATES_PATH = '/home/new-templates';

/**
 * Scope a selector to a field inside either dialog role.
 *
 * 🔴 NOT `` `${DIALOG} ${selector}` ``. A comma-separated selector list splices
 * into a template literal as two INDEPENDENT selectors — the first of which
 * matches the dialog container itself, which precedes its own descendants in
 * document order and so wins `resolveAnchor`'s first-visible-match. The ring then
 * frames the whole drawer, `fillField`'s element-type check fails on a `<div>`,
 * and the dummy value silently never lands. Distribute the combinator instead.
 * (Documented at length in `whatsappCampaign.ts`, which hit exactly this.)
 */
const inDialog = (selector: string): string =>
  `[role="dialog"] ${selector}, [role="alertdialog"] ${selector}`;

/** Both drawers are `FloatingDrawer`s, i.e. Radix Dialogs — hence the roles. */
const DIALOG = '[role="dialog"], [role="alertdialog"]';

const buttonLabelled = (label: string, within: string): AnchorSpec => ({
  structural: { within, match: 'button', labels: [label] },
});

/* ------------------------------------------------------------------ *
 * The module shell.
 * ------------------------------------------------------------------ */

/** The tab strip `AutomationsWorkspaceShell` renders — a real testid it owns. */
const TAB_STRIP = '[data-testid="automations-network-tabs"]';

/** One tab, by the word on it. Scoped to the strip so no other button can match. */
const tab = (label: string): AnchorSpec => buttonLabelled(label, TAB_STRIP);

const CAMPAIGNS_TAB = tab('Campaigns');
const TEMPLATES_TAB = tab('Templates');

/**
 * The sidebar row that opens the module.
 *
 * 🔴 A TOP-LEVEL ROW, NOT A SUBMENU ONE — and this flow shipped broken the first
 * time for assuming otherwise. `new-campaigns` is named explicitly in
 * `AppSidebar`'s `isSingleNode` list, so its group renders as a DIRECT nav button
 * with no accordion and no sub-list: clicking the row navigates. A
 * `submenuAnchor` therefore resolves nothing (there are no sub-rows to find, and
 * `openAccordion` has no trigger to press), `beforeStart` never navigated, and
 * every step after it pointed at a page that was never opened.
 *
 * The sibling flows use `submenuAnchor` because THEIR modules (Campaigns,
 * Automations) genuinely have several destinations to disambiguate between.
 */
/*
  🔴 "Campaigns" IS THE ROW'S REAL LABEL — see the matching note on this
  module's stop in `tours.ts`. The group id is `new-campaigns`, its title is
  not: it inherited "Campaigns" when the old `marketing` group was hidden.
  Anchored on "New Campaigns" this resolved to nothing, so `beforeStart`'s
  `clickWhenReady` never navigated and every step below opened against whatever
  page the user happened to be on — the exact failure the warning there
  describes. Alias array, current name first.
*/
const SIDEBAR_ROW = sidebarAnchor(['Campaigns', 'New Campaigns']);

/** Each board's own create button. */
const NEW_CAMPAIGN = buttonLabelled('New Campaign', 'body');
const NEW_TEMPLATE = buttonLabelled('New Template', 'body');

/**
 * Arrival tests — route AND a control from the page, never one alone.
 *
 * 🔴 BOTH HALVES ARE LOAD-BEARING, and each has already caused a real bug in a
 * sibling flow. A button-only test ("can I see New Campaign?") is satisfied by
 * the identically-labelled button on ANOTHER page, so the flow skips its own
 * navigation and then rings the wrong one. A route-only test passes the moment
 * the URL commits, which is before the page has rendered anything to point at.
 */
const onCampaignsTab = () =>
  window.location.pathname.startsWith(CAMPAIGNS_PATH) && Boolean(resolveAnchor(NEW_CAMPAIGN));
const onTemplatesTab = () =>
  window.location.pathname.startsWith(TEMPLATES_PATH) && Boolean(resolveAnchor(NEW_TEMPLATE));

/* ------------------------------------------------------------------ *
 * The template drawer.
 * ------------------------------------------------------------------ */

/**
 * The AI panel's opt-in trigger, and its Hide control.
 *
 * 🔴 THE COMPOSER NO LONGER OPENS ON THE AI PANEL, and the flow shipped a step
 * that assumed it did. `showAiPanel` is initialised FALSE — the drawer opens on
 * the empty form with a slim "Generate with AI" strip above it, and the
 * panel only exists once that is pressed. The old `template-skip-ai` step pointed
 * at a Hide button that is not on screen and declared `satisfied: form is
 * visible`, which is true the instant the drawer opens — so as an `auto` step it
 * was silently skipped on EVERY run, taking its explanation of the AI route with
 * it. It is replaced by `template-ai-option`, which rings the button that is
 * actually there.
 *
 * 🔴 IDENTIFIED BY `data-testid="template-generate-ai"`. The collapsed
 * control used to be a purple outline button (`border-purple-200
 * text-purple-700`); the walkthrough keyed off those colour classes because
 * the accessible label concatenates "Generate with AIBeta". The strip is
 * now green, so colour classes are the wrong hook — the test id is stable
 * and unique in the drawer.
 */
const AI_OPEN = inDialog('[data-testid="template-generate-ai"]');
const AI_HIDE = buttonLabelled('Hide', DIALOG);
const AI_PROMPT = inDialog('#ai-prompt');
const aiPanelOpen = (): boolean => Boolean(findVisible(AI_PROMPT));

/**
 * The template name input, by its placeholder.
 *
 * 🔴 IT HAS NO `id` AND NO TESTID — `FormInput` (the redesign's own primitive)
 * takes neither. The placeholder is the only stable hook on it. It is shared with
 * the OLD composer, which is harmless here: the two live on different routes and
 * this flow never leaves `/home/new-templates` while the drawer is open, and the
 * selector is dialog-scoped besides.
 */
const TEMPLATE_NAME = inDialog('input[placeholder="my_template_name"]');

/**
 * One of the four fields on the Template Information row, framed as a whole —
 * its label, its info icon and its control together.
 *
 * 🔴 FOUND BY ITS OWN INFO BUTTON, because three of the four have NO id in the
 * mode this flow runs in: `id="category"` is set only in ADMIN mode, and the
 * layout and language controls are `SelectInput`s, which pass no id at all. Each
 * field does carry a uniquely-labelled info popover trigger, and `FormField`'s
 * root (`flex-1 min-w-0 flex flex-col …`) is the smallest element that contains
 * one — the row above it has `flex-col` but no `min-w-0`, and the card above that
 * has neither, so the match can never climb past the field it means.
 *
 * These three are ringed and explained, never CHANGED: every one of them is a
 * Radix `Select`, which opens on a synthetic pointer sequence its trigger
 * inspects rather than on a plain click (see `chooseFirstOption` in `formFill`),
 * and all three already mount on the value this template wants.
 */
const basicField = (infoButtonLabel: string): AnchorSpec => ({
  structural: {
    within: DIALOG,
    match: 'div.min-w-0.flex-col',
    containsSelector: `[aria-label="${infoButtonLabel}"]`,
  },
});

const TEMPLATE_CATEGORY = basicField('What each category means');
const TEMPLATE_LAYOUT = basicField('What each layout means');
const TEMPLATE_LANGUAGE = basicField('About template language');

/**
 * The header-format choices — a RADIO ROW, and matched by id.
 *
 * 🔴 THIS IS WHAT STALLED THE FLOW AT "Write the header". Header Type used to be a
 * row of labelled chips, so both anchors here matched `button` by its own visible
 * text ("Text", "Image", …). It is now an `IconRadioRow`: Radix renders each option
 * as a `button[role="radio"]` holding nothing but the indicator, with the word in a
 * SIBLING `<span>` inside the wrapping `<label>`. A button whose accessible text is
 * empty matches no label, so `enableTextHeader` clicked nothing, the header stayed
 * on None, `input[placeholder="Enter header text"]` never mounted — and the step's
 * `satisfied` gate could never come true. The tour parked on step 28 with "Give the
 * header a line to carry on." and a Next that would not move, which is the worst
 * shape this failure can take: the flow is stuck rather than a step being skipped.
 *
 * 🔴 SO THE ANCHOR IS THE ID, NOT THE COPY. `IconRadioRow` mints `<idPrefix>-<value>`
 * on every option, and the drawer passes `idPrefix="header-type"` — a real, stable
 * hook that survives both re-wording and the chip → radio change that broke this.
 * `value` is Meta's own format enum (`NONE`, `TEXT`, `IMAGE`, …), not a display
 * string, so it cannot drift with the label.
 *
 * Safe from the carousel's own `carousel-header-format` row: different prefix, and it
 * only renders under `templateLayout === "CAROUSEL"`, which this flow never selects.
 */
const headerTypeRadio = (value: string): AnchorSpec => ({
  selector: inDialog(`#header-type-${value}`),
});
const HEADER_TEXT_CHIP = headerTypeRadio('TEXT');
/**
 * The row itself, as one ring: the nearest ancestor shared by the two options this
 * flow actually depends on. NONE is hard-coded by the drawer and TEXT is the format
 * the next step writes into, so the ring cannot fail while the step it belongs to is
 * still possible — the failure mode a `group` over all five options would add (every
 * sub-anchor has to hit) buys nothing here.
 */
const HEADER_CHIPS: AnchorSpec = {
  group: [headerTypeRadio('NONE'), headerTypeRadio('TEXT')],
};

/** The header's own line. Exists only while a TEXT header is switched on. */
const HEADER_TEXT = inDialog('input[placeholder="Enter header text"]');
/**
 * Is a text header on?
 *
 * Read off the INPUT rather than off the radio's own state. The radio only says
 * which format is selected, so a checked TEXT option and a mounted header field are
 * two different claims — while the input exists if and only if
 * `header.enabled && header.format === "TEXT"`, which is exactly the question, and
 * it is the field the next step has to type into anyway.
 */
const textHeaderOn = (): boolean => Boolean(findVisible(HEADER_TEXT));

/**
 * Switch the text header on. A no-op when it already is.
 *
 * The click lands on the radio itself rather than on its `<label>`: both toggle the
 * group, but the label's box includes the icon and the word, and a dispatched click
 * there is one more indirection to be wrong about.
 */
const enableTextHeader = async (): Promise<void> => {
  if (textHeaderOn()) return;
  await clickWhenReady(HEADER_TEXT_CHIP);
  await waitUntil(textHeaderOn, 2000);
};

/** The body textarea DOES carry an id, and the same one as the old composer. */
const TEMPLATE_BODY = inDialog('#body-text');

/**
 * One variable's sample box.
 *
 * Unlike the campaign drawer's variable grid — where three boxes share one
 * placeholder and have to be reached by position — every sample input here names
 * its own variable in its placeholder, so each has a selector of its own with no
 * positional guessing.
 */
const variableSample = (position: number): string =>
  inDialog(`input[placeholder="Sample text for {{${position}}}"]`);

/** The whole Variable Samples panel — its heading, its warning and its boxes. */
const SAMPLES_PANEL: AnchorSpec = {
  structural: {
    within: DIALOG,
    match: 'div.bg-gray-50.rounded-lg',
    containsSelector: 'input[placeholder^="Sample text for"]',
  },
};

const sampleBoxes = (): HTMLInputElement[] =>
  Array.from(
    document.querySelectorAll<HTMLInputElement>(inDialog('input[placeholder^="Sample text for"]')),
  );

/** True with no variables at all — `[].every` — which is the honest answer. */
const allSamplesFilled = (): boolean => sampleBoxes().every((box) => box.value.trim() !== '');

/** The footer line. Pre-filled by the composer for a marketing template. */
const TEMPLATE_FOOTER = inDialog('#footer-text');

/** The buttons list: one control to add a row, one field per row. */
const ADD_BUTTON = buttonLabelled('Add Button', DIALOG);
const QUICK_REPLY_TEXT = inDialog('input[placeholder="Quick reply text"]');
const quickReplyRowAdded = (): boolean => Boolean(findVisible(QUICK_REPLY_TEXT));

/** The drawer's own footer action. Its label changes only in edit mode. */
const TEMPLATE_SUBMIT = buttonLabelled('Create Template', DIALOG);

const templateFormVisible = () => Boolean(findVisible(TEMPLATE_NAME));
const templateDrawerOpen = () => templateFormVisible() || aiPanelOpen();

/* ------------------------------------------------------------------ *
 * The campaign drawer.
 * ------------------------------------------------------------------ */

const CAMPAIGN_NAME = inDialog('input[placeholder="Enter campaign name"]');
const campaignFormOpen = () => Boolean(findVisible(CAMPAIGN_NAME));

/** The template picker's trigger — a testid the drawer already exposes. */
const TEMPLATE_TRIGGER = '[data-testid="campaign-template-trigger"]';
/** Its placeholder, from the drawer. The only "nothing chosen yet" signal. */
const TEMPLATE_PLACEHOLDER = 'Select template';
/** Its search box, which only exists while the picker is open. */
const TEMPLATE_SEARCH = '[data-testid="campaign-template-search"]';
const templatePickerOpen = () => Boolean(findVisible(TEMPLATE_SEARCH));

/**
 * The picker's option rows.
 *
 * 🔴 NOT `[role="option"]`, which is what `chooseFirstOption` defaults to. This
 * drawer's picker is NOT the app's `SearchableSelect`: it is a hand-rolled Popover
 * whose rows are plain `<button>`s with no ARIA role at all, so the default
 * selector matches nothing and the pick silently never happens. `.overflow-y-auto`
 * is the row container's own Tailwind class inside Radix's popper wrapper — the
 * rows are still ordinary buttons, so a plain click selects one (the property that
 * makes automating this safe, unlike a Radix `Select`).
 */
const TEMPLATE_OPTION = '[data-radix-popper-content-wrapper] .overflow-y-auto > button';

/** The audience source radios. Real ids, unlike most of this drawer. */
const AUDIENCE_ALL = inDialog('#audience-tab-all');

/**
 * All four audience sources as ONE ring, so the step about who receives the
 * campaign frames the whole choice rather than one radio dot with the other three
 * options dimmed beside it. A `group` anchor resolves each radio and rings their
 * nearest shared ancestor — which is the drawer's own `RadioGroup` row — without
 * asking the drawer for a wrapper it has no other reason to have.
 */
const AUDIENCE_SOURCES: AnchorSpec = {
  group: [
    { selector: inDialog('#audience-tab-upload') },
    { selector: AUDIENCE_ALL },
    { selector: inDialog('#audience-tab-tags') },
    { selector: inDialog('#audience-tab-stages') },
  ],
};

/**
 * The "All contacts" row.
 *
 * 🔴 A `labelWithin` MATCH, because structural label matching is EXACT against
 * the whole accessible label — and this row's text is "All contacts" + its
 * subtitle + the state pill, so `labels: ['All contacts']` alone can never match
 * it. The `<span>` holding the title is what carries that exact text.
 */
const ALL_CONTACTS_ROW: AnchorSpec = {
  structural: { within: DIALOG, match: 'button', labels: ['All contacts'], labelWithin: 'span' },
};

/**
 * Is "All contacts" actually selected?
 *
 * The row is a plain `<button>` whose selected marker is an icon-only tick
 * (no "Selected" label). `aria-pressed` is the DOM signal the drawer sets.
 */
const allContactsSelected = (): boolean =>
  resolveAnchor(ALL_CONTACTS_ROW)?.element.getAttribute('aria-pressed') === 'true';

/** The send button, by its `form` attribute — its label changes with the mode. */
const CAMPAIGN_SUBMIT = { selector: `button[type="submit"][form="campaign-form"]` };
/** The split button's chevron, which is where scheduling lives. */
const SEND_OPTIONS = { selector: inDialog('button[aria-label="Choose send option"]') };

/**
 * The whole split button as one ring, so the step about WHEN a campaign goes out
 * frames both halves — the immediate send and the chevron that turns it into a
 * scheduled one — rather than a lone button with the actual choice cropped off.
 */
const SEND_CONTROL: AnchorSpec = { group: [CAMPAIGN_SUBMIT, SEND_OPTIONS] };

/**
 * The variable-mapping grid — one cell per `{{1}}` in the chosen template.
 *
 * `.grid.gap-3` is the drawer's ONLY element carrying both classes (every other
 * `gap-3` in it is a flex row), so this needs no id the host does not have.
 */
const VARIABLES_GRID = inDialog('.grid.gap-3');

/**
 * One variable's constant-value box, by position.
 *
 * 🔴 INDEXED, BECAUSE ALL THREE BOXES SHARE ONE PLACEHOLDER. `fill` resolves each
 * selector with the tour's first-visible-match rule, so three entries reading
 * `input[placeholder="Enter value"]` would all write to variable one — the first
 * box would take the last value and the other two would stay empty, which is
 * precisely the "the dummy data never arrived" symptom, with the field it did
 * arrive in holding the wrong words.
 *
 * The cells are the grid's own children (`FormField` renders a `<div>`), so
 * `nth-of-type` counts them exactly. The mapping `Select` beside each box is a
 * Radix `Select` and is deliberately left alone — see `chooseFirstOption` in
 * `formFill` for why one of those cannot be driven by a plain click.
 */
const variableValue = (position: number): string =>
  inDialog(`.grid.gap-3 > div:nth-of-type(${position}) input[placeholder="Enter value"]`);

/** Every constant-value box currently on screen, however many the template has. */
const variableBoxes = (): HTMLInputElement[] =>
  Array.from(document.querySelectorAll<HTMLInputElement>(inDialog('input[placeholder="Enter value"]')));

/**
 * Is every blank filled?
 *
 * True for a template with NO variables (`[].every` is true), which is the
 * honest answer: there is nothing left to supply. The step itself is dropped in
 * that case anyway, because its grid never renders.
 */
const allVariablesFilled = (): boolean => variableBoxes().every((box) => box.value.trim() !== '');

/** The link-shortening toggle. Only rendered for a template with variables or URL buttons. */
const TRACK_CLICKS = inDialog('#track-clicks');

/**
 * The WhatsApp preview in the right-hand column.
 *
 * `.preview-chat-scroll` is the phone chat area in the create-campaign preview
 * rail (and `EnhancedTemplatePreview` compact/full elsewhere) — the surrounding
 * frame is identified only by arbitrary-value Tailwind classes (`rounded-[3rem]`)
 * that need escaping to appear in a selector at all.
 */
const PREVIEW = inDialog('.preview-chat-scroll');

/**
 * The estimated-cost row inside the Contacts card.
 *
 * It has no id, so this matches on shape plus the one thing inside it that is
 * uniquely identifiable: the hover-card trigger's `aria-label`. Both halves are
 * needed — `div.rounded-xl.px-4.py-3` alone is a Tailwind coincidence waiting to
 * be shared, and `containsSelector` alone would match the first ancestor that
 * happens to contain the icon.
 */
const COST_ROW: AnchorSpec = {
  structural: {
    within: DIALOG,
    match: 'div.rounded-xl.px-4.py-3',
    containsSelector: '[aria-label="Cost breakdown"]',
  },
};

/**
 * The Advanced Settings collapse control.
 *
 * Its own text is the state it will move TO, so both words are accepted: "Show"
 * while collapsed, "Hide" once open. It is the drawer's only button labelled
 * either — "Show AI Campaign Predictions" is a `<label>`, not a button.
 */
const ADVANCED_TOGGLE: AnchorSpec = {
  structural: { within: DIALOG, match: 'button', labels: ['Show', 'Hide'] },
};

const RETRY_TOGGLE = inDialog('#retry-failed-enabled');
const OPTED_OUT_TOGGLE = inDialog('#include-opted-out-contacts');
const DESCRIPTION = inDialog('#description');
const PREDICTIONS = inDialog('#show-predictions');

/**
 * Is the Advanced Settings panel open?
 *
 * Tested by one of the controls INSIDE it rather than by the toggle's own word:
 * the toggle is what the user (or a previous step) presses, and reading the
 * effect rather than the button is what makes every `prepare` below idempotent.
 */
const advancedOpen = (): boolean => Boolean(findVisible(OPTED_OUT_TOGGLE));

/** Open the Advanced Settings panel if it is collapsed. Safe to call when it is not. */
const openAdvanced = async (): Promise<void> => {
  if (advancedOpen()) return;
  await clickWhenReady(ADVANCED_TOGGLE);
  await waitUntil(advancedOpen, 2000);
};

/**
 * The send-options popover, while it is open.
 *
 * Portalled to `document.body`, so it is NOT reachable through `inDialog` — the
 * same reason `TEMPLATE_OPTION` above reaches for the popper wrapper. `w-80` +
 * `rounded-xl` is `PopoverContent`'s own class list; the cost hover-card next to
 * it is `w-80 rounded-2xl`, so the two cannot be confused.
 */
const SEND_OPTIONS_POPOVER = '[data-radix-popper-content-wrapper] .w-80.rounded-xl';
const sendOptionsOpen = (): boolean => Boolean(findVisible(SEND_OPTIONS_POPOVER));

/**
 * Toggle the send-options popover shut.
 *
 * 🔴 IT DOES NOT CLOSE ITSELF WHEN THE CALLOUT IS CLICKED, and that is by design
 * elsewhere: `modalSafeChromeProps` stops the flow's own chrome from reaching
 * Radix's document-level dismiss listener, so pressing Next leaves the popover
 * standing — directly over the send button the next step has to point at.
 */
const closeSendOptions = async (): Promise<void> => {
  if (!sendOptionsOpen()) return;
  await clickWhenReady(SEND_OPTIONS);
  await waitUntil(() => !sendOptionsOpen(), 2000);
};

/* ------------------------------------------------------------------ *
 * Dummy data.
 * ------------------------------------------------------------------ */

const two = (n: number) => String(n).padStart(2, '0');

/**
 * Names that pass Meta's charset rule AND the drawer's own duplicate check
 * (`existingTemplateNames.some(...)`, case-insensitive).
 *
 * 🔴 STAMPED ONCE PER RUN, IN `beforeStart` — not per attempt, and not at module
 * load. A `fill` is re-attempted until the value reads back, so computing the name
 * per attempt would type a different string each time and never settle. But
 * computing it at module load pins ONE name for the whole browser session, so a
 * second run of the flow without a page reload types a name that now exists and the
 * drawer refuses to submit — a dead end on the last template step, with the reason
 * only visible in the form's own validation. `beforeStart` runs exactly once per
 * run, which is the granularity that satisfies both.
 */
let templateNameValue = '';
let campaignNameValue = '';

function stampRunNames(): void {
  const now = new Date();
  const stamp = `${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
  templateNameValue = `festive_offer_${stamp}`;
  campaignNameValue = `Festive offer ${two(now.getHours())}:${two(now.getMinutes())}:${two(
    now.getSeconds(),
  )}`;
}

// Stamped now as well, so a flow resumed from a persisted run — or any future caller
// that reaches a step without `beforeStart` — still has a usable value rather than
// filling the field with an empty string.
stampRunNames();

/**
 * The campaign's internal note. Optional on the form, filled here because the step
 * exists to show WHERE the note lives and who can read it — an empty box would
 * demonstrate neither.
 */
const CAMPAIGN_DESCRIPTION_VALUE =
  'Festive sale push to every saved contact. Internal note — nobody outside the team sees this.';

/** One line, under the sixty-character header cap, and no variable in it. */
const TEMPLATE_HEADER_VALUE = 'Festive Sale is live 🎉';

/**
 * A body Meta would accept, WITH BLANKS IN IT.
 *
 * 🔴 THE VARIABLES ARE THE POINT, and the previous value had none. A template is
 * "a message with blanks in it" — that is what the campaign flow's own picker step
 * says it is — but this body was flat text, so the composer's Variable Samples
 * panel never rendered and the walkthrough could not show the one mechanism that
 * separates a template from a plain message.
 *
 * 🔴 NEITHER END MAY BE A VARIABLE. `isFormValid` rejects a body that starts or
 * ends with `{{…}}` outright (and separately rejects a leading period before one),
 * so the blanks sit inside the sentence with words either side.
 *
 * Two of them rather than one, so the numbering rule is visible: they are
 * POSITIONAL (`{{1}}`, `{{2}}`), and mixing those with named blanks in one body is
 * what `validateVariables` refuses.
 */
const TEMPLATE_BODY_VALUE =
  'Hi {{1}}, our festive sale is now live — {{2}} off everything until Sunday. ' +
  'Reply SHOP and we will send you the collection.';

/**
 * What each blank stands for, for Meta's reviewer.
 *
 * Realistic but fictional on purpose: the panel's own warning is not to put
 * customer information in a sample, and these are read by a human at Meta.
 */
const TEMPLATE_SAMPLE_VALUES = ['Priya', '20 percent'];

/** The quick reply's label — the tap that replaces "Reply SHOP" in the body. */
const TEMPLATE_BUTTON_VALUE = 'Shop now';

export const NEW_CAMPAIGNS_MODULE_TASK: TaskFlow = {
  id: 'walk-new-campaigns',
  stepId: 'new-campaigns',
  cta: 'Try it: campaigns and templates',
  preparingLabel: 'Opening Campaigns…',
  title: 'New Campaigns — campaigns and templates',
  summary: 'One tab at a time: a campaign, then the wording behind it.',
  doneTitle: 'Campaign sent, template written',
  doneDescription:
    'You built a campaign on the Campaigns tab and wrote the kind of template it sends on the Templates tab — the two halves of this module and the dependency between them. Nothing left this browser: both are written to your demo data.',

  /**
   * Open the module before any of the flow's own chrome appears.
   *
   * The PRODUCT TOUR has just finished pointing at this exact sidebar row, so
   * re-highlighting it as this flow's step one would read as padding. Nothing has
   * to be expanded first — see `SIDEBAR_ROW`: this module's row is a direct link.
   */
  beforeStart: async () => {
    // A fresh name pair per run — see `stampRunNames`.
    stampRunNames();
    if (onCampaignsTab() || onTemplatesTab()) return;
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onCampaignsTab, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "walk-new-campaigns": never confirmed arrival on /home/new-campaigns ' +
          '(route and/or New Campaign button). The first step may resolve nothing.',
      );
    }
  },

  /**
   * Close the composer this flow opened — see `TaskFlow.afterEnd` and
   * `closeChrome.ts`.
   *
   * 🔴 THIS IS THE HALF "SKIP MODULE" WAS MISSING. The flow opens the campaign
   * drawer and then the template drawer, and the engine's teardown closes neither
   * — so skipping out of either resumed the tour with a Radix modal still owning
   * the screen, and the next module's highlight was painted under its backdrop.
   *
   * BOTH drawers are named, not just the campaign one: this is a two-segment
   * walkthrough and Skip module is reachable from any step of either. A run that
   * SUBMITTED its campaign and is midway through the template is the ordinary
   * case, and the campaign predicate is simply false by then.
   *
   * Neither drawer has created anything until it is submitted, so closing one
   * discards only draft input — and on this flow that input was typed by the
   * guide. `closeFlowChrome` peels a nested picker first when one is open.
   */
  afterEnd: async () => {
    await closeFlowChrome(() => campaignFormOpen() || templateDrawerOpen());
  },

  steps: [
    /* ---------------- Tab one: Campaigns ---------------- */

    {
      id: 'campaigns-tab',
      label: 'Campaigns',
      title: 'Campaigns — where a send is put together',
      description:
        'This is the redesigned Campaigns module, and everything in it sits under the two tabs here. This one, Campaigns, is the sends themselves — who got what, and how it performed. Templates, beside it, is the wording those sends use, each piece approved by Meta before it can go out. We will take one tab at a time, starting with a campaign built from wording this account already had approved.',
      anchor: CAMPAIGNS_TAB,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      /**
       * 🔴 WITHOUT THIS THE FLOW OPENS ON NOTHING. `beforeStart` lands on the
       * Campaigns tab, so `satisfied()` is already true the instant this step is
       * entered — and `TaskEngine.enter()` takes its fast path for an
       * already-satisfied step: no `waitForTarget`, no hold, no ring. The one step
       * that is supposed to be seen FIRST would be the one step never drawn.
       */
      forceHighlight: true,
      /** Only does anything if the user wandered off the tab before pressing. */
      onNext: async () => {
        if (onCampaignsTab()) return;
        await clickWhenReady(CAMPAIGNS_TAB);
        await waitUntil(onCampaignsTab, 5000);
      },
      satisfied: onCampaignsTab,
      nextLabel: 'Try it: build a campaign',
      /**
       * Not interested in building one? Jump straight to the other tab's gate.
       *
       * 🔴 REQUIRED BECAUSE `nextLabel` REPLACED "Next". With the only button
       * reading "Try it: build a campaign", the way past this segment was one press
       * of Skip PER CAMPAIGN STEP — twenty of them, now that the flow walks the whole
       * form. This makes each tab independently skippable, which is the whole point of
       * gating them separately.
       */
      skipTo: 'templates-tab',
    },

    {
      id: 'campaign-open',
      label: 'New campaign',
      title: 'Start a campaign',
      description:
        'Every card on this board is one broadcast, with its delivery numbers. A campaign sends one approved template to many people at once, so the form asks three things: what to send, who to send it to, and when.',
      anchor: NEW_CAMPAIGN,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      prepare: async () => {},
      onNext: async () => {
        if (campaignFormOpen()) return;
        await clickWhenReady(NEW_CAMPAIGN);
      },
      satisfied: campaignFormOpen,
      /**
       * Back over this step closes what its Next opened — see `TaskStep.rewind`.
       *
       * Without it, Back from the step after this one landed HERE with the
       * campaign composer still covering the New Campaign
       * button this step is about, which `resolve()` then refuses to ring: a step on screen with nothing
       * highlighted, which is what "Back disturbs the tour" looks like.
       */
      rewind: async () => {
        await closeFlowChrome(campaignFormOpen, 'rewind');
      },
      nextLabel: 'Open the campaign form',
      waitingHint: 'Press the button to open the campaign form.',
    },

    {
      id: 'campaign-name',
      label: 'Name',
      title: 'Name the campaign',
      description:
        'Filled in for you. Unlike a template name this one is for your own reporting only — nobody outside your team sees it, and there are no formatting rules.',
      anchor: { selector: CAMPAIGN_NAME },
      side: 'right',
      spotlight: true,
      fill: [[CAMPAIGN_NAME, campaignNameValue]],
      satisfied: () => isFilled(CAMPAIGN_NAME),
      waitingHint: 'Give the campaign a name to carry on.',
      optional: false,
    },

    {
      id: 'campaign-template-open',
      label: 'Template',
      title: 'Choose the template',
      description:
        'Here is the dependency the Templates tab exists for: a campaign sends one approved template, so this list holds only APPROVED ones — never a pending submission, and never an authentication template, which Meta reserves for one-time codes. Every entry in it was written on the Templates tab, which we open once this campaign is away. Opening the picker.',
      anchor: { selector: TEMPLATE_TRIGGER },
      side: 'right',
      spotlight: true,
      /**
       * Safe here, unlike on the pick step below: this step's target is the
       * trigger BUTTON, which exists whether or not a template has been chosen.
       * Without it, a template already in the form's state from an earlier run in
       * this tab would make the step satisfied on entry and skip its ring.
       */
      forceHighlight: true,
      /**
       * 🔴 A NO-OP `prepare` PLUS AN `onNext`, NOT AN `advance: 'auto'` THAT DOES
       * THE WORK ITSELF — the same shape `campaign-open` two steps above already
       * uses, and for the same reason.
       *
       * As an auto step this was on screen for `PRE_ACTION_HOLD_MS` and no longer:
       * the ring appeared, `prepare` opened the picker, the step was satisfied and
       * the engine advanced. Measured against its neighbours at 1440x820, steps 4
       * and 5 lasted ~540ms each while steps 1, 3 and 6 lasted 900–1500ms — so the
       * two steps that explain WHERE THE TEMPLATE LIST COMES FROM, which is the
       * dependency this whole module exists to teach, were the two the user never
       * got to read. The report was exactly that: it "directly jumps to step 6".
       *
       * `prepare` MUST STAY A FUNCTION even though it does nothing. Its presence is
       * what makes `TaskEngine.enter` take the `waitForTarget` path and hold the
       * ring on the trigger; without it the step falls to the already-satisfied
       * fast path and is never pointed at.
       *
       * The click moves to `onNext`, so it happens when the USER asks rather than
       * on the engine's own clock. Nothing is taken away: the guide still opens the
       * picker for them, and their own click on the real dropdown still works —
       * `onNext` sees the picker already open and does nothing.
       */
      prepare: async () => {},
      /**
       * 🔴 GATED ON THE PICKER BEING OPEN, NOT ON A TEMPLATE BEING CHOSEN — and the
       * difference is what keeps the NEXT step pointable.
       *
       * The form's React state survives a re-run in the same tab, so a second run
       * can reach here with a template already selected. The old `|| isChosen(...)`
       * meant Next then did nothing at all, the picker stayed shut, and
       * `campaign-template-pick` (whose target lives INSIDE that picker) had
       * nothing to ring. As an auto step that was invisible — it just advanced —
       * but a step that waits for the user would sit there pointing at nothing.
       * Opening it regardless costs a re-run one extra glance at a list it is about
       * to explain anyway.
       */
      onNext: async () => {
        if (templatePickerOpen()) return;
        await clickWhenReady({ selector: TEMPLATE_TRIGGER });
      },
      nextLabel: 'Open the template list',
      /**
       * Also satisfied once a template is CHOSEN, not only while the picker is
       * open — picking closes it. Otherwise pressing Back past a completed choice
       * would reopen a picker nobody asked to see and leave it open while the flow
       * moved on.
       */
      satisfied: () => templatePickerOpen() || isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER),
      waitingHint: 'Press Next to open the template list.',
    },

    {
      id: 'campaign-template-pick',
      label: 'Template',
      title: 'Pick an approved template',
      description:
        'Picked for you — the first approved template in the account. Change it from the same dropdown whenever you want; the preview on the right redraws to match, exactly as the customer will see it.',
      anchor: { selector: TEMPLATE_SEARCH },
      side: 'right',
      spotlight: true,
      /**
       * 🔴 NO `forceHighlight`, deliberately — the opposite call from the step
       * above. This target only exists while the picker is OPEN, and an
       * already-chosen template has closed it, so forcing the highlight would
       * stall on `waitForTarget` for its full timeout with nothing to point at.
       */
      /**
       * 🔴 DO NOT ACCEPT THIS TARGET UNTIL THE POPOVER HAS BEEN POSITIONED.
       *
       * `waitForTarget` measures and publishes ONCE — on the first frame the anchor
       * resolves — and `enter()` then sleeps `PRE_ACTION_HOLD_MS` with the tick loop
       * held off by its `entering` guard, so nothing re-measures for that whole
       * stretch. This target lives inside a Radix popover, which mounts before
       * floating-ui has placed it and therefore measures at wherever an unpositioned
       * fixed layer parks. Measured at 1440x820: the engine published
       * (46, -453.75) 152x19.5 and held it for 525ms — 454px ABOVE the top of the
       * window — while the search box itself read (412, 268) 554x19 from the very
       * first sample onward. The ring was drawn off-screen for the step's whole
       * life and only snapped into place once the tick loop resumed.
       *
       * `ready()` is the hook that fixes it: `waitForTarget` re-resolves every frame
       * and publishes on the first frame this agrees, so the rect that reaches the
       * ring is one taken after the popover settled. Testing the TOP-LEFT rather
       * than full containment on purpose — the failure mode is an unpositioned layer
       * parked at large negative coordinates, and demanding the whole box fit the
       * window would strand the step on a short screen.
       */
      ready: () => {
        const box = findVisible(TEMPLATE_SEARCH)?.getBoundingClientRect();
        return Boolean(box && box.top >= 0 && box.left >= 0);
      },
      /**
       * 🔴 THE NO-OP `prepare` IS ALSO WHAT FIXES THE RING ON THIS STEP, not only
       * its pacing.
       *
       * This target lives inside the picker popover, which Radix mounts and then
       * ANIMATES into place. `waitForTarget` accepts it the moment it exists —
       * mid-animation — so the rect first measured is the one the transform was
       * passing through. Measured at 1440x820: the ring settled at (46, -454) 152x20
       * while the real search box was at (412, 268) 554x20, i.e. 454px ABOVE the top
       * of the window, and it stayed there for the step's whole 520ms life. Step 6
       * then spent its first ~700ms travelling back down from off-screen.
       *
       * The tick loop re-measures every `TICK_MS` and the rect is part of the
       * publish signature, so the ring snaps to the right box within ~140ms — it
       * simply never got the chance, because the step was gone first. A step that
       * waits for the user outlives the animation, which turns a permanently wrong
       * highlight into one frame of catch-up.
       *
       * Empty for the same reason as the step above: its presence is what holds the
       * ring at all.
       */
      prepare: async () => {},
      /**
       * 🔴 THE ALREADY-CHOSEN BRANCH CLOSES THE LIST RATHER THAN RETURNING. The step
       * above now opens the picker unconditionally (see its note), so on a re-run
       * this step is entered with a choice already made AND the list open — and a
       * bare early return would leave it hanging open over every step that follows.
       * Clicking the trigger toggles it shut, which is the same control the user
       * would reach for.
       */
      onNext: async () => {
        if (isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER)) {
          if (templatePickerOpen()) await clickWhenReady({ selector: TEMPLATE_TRIGGER });
          return;
        }
        await chooseFirstOption(
          { selector: TEMPLATE_TRIGGER },
          { optionSelector: TEMPLATE_OPTION },
        );
      },
      nextLabel: 'Pick the first approved one',
      satisfied: () => isChosen(TEMPLATE_TRIGGER, TEMPLATE_PLACEHOLDER),
      waitingHint: 'Press Next to pick an approved template.',
      // Required: with no template the form cannot be submitted at all, so
      // skipping would strand the user on the last step.
      optional: false,
    },

    {
      id: 'campaign-variables',
      label: 'Variables',
      title: 'Fill the blanks in the wording',
      description:
        'Filled in for you. Every {{1}} in the template is a blank the campaign has to supply, and each takes one of two things: a constant, which sends the same words to everybody, or a contact field, which is what makes one template read personally to a thousand people. Pick a contact field from the dropdown and the box beside it becomes the fallback used when that contact has nothing stored.',
      anchor: { selector: VARIABLES_GRID },
      side: 'right',
      spotlight: true,
      /**
       * Values for the first three blanks — a name, an order reference and a day,
       * which is what a utility template's variables almost always are. A template
       * with fewer simply leaves the extra entries unresolved, and one with none at
       * all never renders the grid, so this whole step is dropped.
       */
      fill: [
        [variableValue(1), 'Priya'],
        [variableValue(2), 'NR-10482'],
        [variableValue(3), 'Friday'],
      ],
      satisfied: allVariablesFilled,
      waitingHint: 'Give every blank a value to carry on.',
    },

    {
      id: 'campaign-track-clicks',
      label: 'Link tracking',
      title: 'Count who clicks',
      description:
        'Left off, which is the drawer’s own default. Switch it on and every link in the message — inside a variable or behind a button — is rewritten as a short link before the send, which is the only way a WhatsApp campaign can report a click-through rate at all. The trade is that recipients see a shortened URL instead of your own domain.',
      anchor: { selector: TRACK_CLICKS },
      side: 'right',
      spotlight: true,
      // Explanation only — turning it on would rewrite the links in a message the
      // user has not agreed to change, for a number this walkthrough never reads.
    },

    {
      id: 'campaign-preview',
      label: 'Preview',
      title: 'What the customer will see',
      description:
        'The phone on the right redraws every time the template or its blanks change, and it is the only place the header, the body, the footer and the buttons appear stacked the way WhatsApp will actually stack them. Worth reading before every send: wording that looks fine as a row in the template list can still be three lines too long here.',
      anchor: { selector: PREVIEW },
      side: 'left',
      spotlight: true,
    },

    /**
     * Two steps still, but only because the confirmation row is not in the DOM
     * until the All Contacts radio is on (the drawer opens on Upload). The radio
     * now ALSO sets `includeAllContacts` — the inner row is status, not a second
     * apply. Keep them split so the source radios and the "everyone" confirmation
     * each get a ring; do not wait for the row on the radio step.
     */
    /**
     * 🔴 `auto`, NOT `manual`, and that is the fix for a false "skipped" report. The
     * engine PUBLISHES a step — Next button and all — before it resolves the target,
     * holds the ring and runs `prepare`, so on a `manual` step whose `satisfied()`
     * depends on `prepare` having landed, a user (or a script) who presses Next
     * inside that window advances with the predicate still false and the bar records
     * the step as SKIPPED even though its action worked a moment later. An `auto`
     * step has no Next to out-run: it shows, acts, and advances when the state is
     * genuinely true. Reproduced in the automated walkthrough, which pressed Next
     * 1.5s after entry and still beat the Radix re-render.
     *
     * The four-source EXPLANATION therefore lives on the next step, which dwells.
     */
    {
      id: 'campaign-audience-source',
      label: 'Audience',
      title: 'Pick the audience source',
      description:
        'Recipients come from one of four places, and the row of choices here decides which chooser the form shows you. Switching it to Everyone.',
      anchor: AUDIENCE_SOURCES,
      side: 'right',
      spotlight: true,
      // The radios exist regardless of which is picked, so forcing the highlight is
      // safe — and needed, because a drawer left on this source from an earlier run
      // in this tab would otherwise skip the ring entirely.
      forceHighlight: true,
      prepare: async () => {
        if (isChecked(AUDIENCE_ALL)) return;
        ensureChecked(AUDIENCE_ALL);
        await waitUntil(() => isChecked(AUDIENCE_ALL), 1500);
      },
      advance: 'auto',
      satisfied: () => isChecked(AUDIENCE_ALL),
      waitingHint: 'Switching to Everyone…',
      optional: false,
    },

    {
      id: 'campaign-audience-all',
      label: 'Everyone',
      title: 'Choose who gets it',
      description:
        'Selected for you. Choosing All Contacts above already committed everyone as the audience — this row is the confirmation, not a second click. The other three sources are an uploaded spreadsheet, a set of tags, or a CRM stage, and any of those is the swap you would make on a real send that should not go to the whole list.',
      anchor: ALL_CONTACTS_ROW,
      side: 'right',
      spotlight: true,
      forceHighlight: true,
      /**
       * Re-asserts the source too. The step before this one selects it, but a user
       * who skips that step (or presses Next through it) would otherwise arrive here
       * with the Upload panel still on show and no row to click — so this step
       * guarantees its own precondition rather than trusting its predecessor.
       * Idempotent: `ensureChecked` and the click both no-op when already done.
       */
      prepare: async () => {
        if (!isChecked(AUDIENCE_ALL)) {
          ensureChecked(AUDIENCE_ALL);
          await waitUntil(() => Boolean(resolveAnchor(ALL_CONTACTS_ROW)), 2000);
        }
        if (allContactsSelected()) return;
        await clickWhenReady(ALL_CONTACTS_ROW);
        await waitUntil(allContactsSelected, 1500);
      },
      satisfied: () => allContactsSelected(),
      waitingHint: 'Choose some recipients to carry on.',
      optional: false,
    },

    {
      id: 'campaign-cost',
      label: 'Cost',
      title: 'What this send will cost',
      description:
        'Recipients multiplied by the per-conversation rate for this template’s CATEGORY — marketing costs more than utility, which is why the category you give a template decides the bill as much as the audience does. The info icon opens the breakdown. It moves as the audience does, so this is the number to read before pressing send rather than after.',
      anchor: COST_ROW,
      side: 'right',
      spotlight: true,
    },

    {
      id: 'campaign-advanced-open',
      label: 'Advanced',
      title: 'Open the advanced settings',
      description:
        'Everything a first campaign can safely ignore lives behind this toggle, collapsed so the form stays short: what to do about recipients the send fails to reach, whether people who opted out are messaged at all, and a note for your own team. We will open it and take the three in turn.',
      anchor: ADVANCED_TOGGLE,
      side: 'right',
      spotlight: true,
      /**
       * The press lives in `onNext`, not `prepare`, for the reason the tab steps
       * give: `prepare` runs on ENTRY, so the panel would already be open behind
       * the callout explaining what opening it does.
       */
      onNext: openAdvanced,
      satisfied: advancedOpen,
      nextLabel: 'Open advanced settings',
      waitingHint: 'Press the button to open the advanced settings.',
    },

    {
      id: 'campaign-retry',
      label: 'Retries',
      title: 'Retry the ones that fail',
      description:
        'Left off. Sends fail for reasons that pass — a handset with no signal, a number briefly unreachable — so switching this on re-attempts only those recipients, up to twice, at least a day apart. Nobody the first pass reached is messaged again, which is what separates a retry from simply sending the campaign twice.',
      anchor: { selector: RETRY_TOGGLE },
      side: 'right',
      spotlight: true,
      /**
       * 🔴 RE-ASSERTS ITS OWN PRECONDITION rather than trusting the step before it.
       * The panel this control lives in is collapsible, so pressing Back from a later
       * step — or collapsing it by hand mid-flow — would otherwise leave this step
       * pointing at an element that is not in the DOM.
       *
       * 🔴 AND DELIBERATELY NO `forceHighlight`, which is the opposite call from the
       * audience steps and for a reason worth stating: `TaskEngine.enter` waits for
       * the target BEFORE `prepare` runs, so on a step whose own `prepare` is what
       * reveals the target, forcing the highlight buys a five-second stall staring at
       * a collapsed panel and nothing else. Without it the engine resolves once, runs
       * `prepare`, and the tick loop draws the ring on the next tick — which is
       * 140ms, not five seconds. The ring still appears either way.
       */
      prepare: openAdvanced,
    },

    {
      id: 'campaign-opted-out',
      label: 'Opted out',
      title: 'Leave the opt-outs alone',
      description:
        'Left off, and it should stay off for a marketing send. Anyone who replied STOP is skipped by default; turning this on messages them anyway, which Meta treats as a policy breach and can suspend the number for. The one legitimate use is a transactional or utility message they separately consented to — an order update, not an offer.',
      anchor: { selector: OPTED_OUT_TOGGLE },
      side: 'right',
      spotlight: true,
      // Same shape as the retry step above, including why there is no
      // `forceHighlight` on a step whose own `prepare` reveals its target.
      prepare: openAdvanced,
    },

    {
      id: 'campaign-description',
      label: 'Note',
      title: 'Leave a note for your team',
      description:
        'Filled in for you, and optional. Nobody outside your team ever sees this — it is the line that explains, three months later, why a campaign called “Festive offer” went to everybody. The campaign NAME at the top is likewise internal; the only text a customer reads is the template.',
      anchor: { selector: DESCRIPTION },
      side: 'right',
      spotlight: true,
      // Same reason as the two steps above: the field only exists while the panel is
      // open, so this step reopens it rather than trusting its predecessor — and for
      // the same reason it does not force the highlight.
      prepare: openAdvanced,
      fill: [[DESCRIPTION, CAMPAIGN_DESCRIPTION_VALUE]],
      satisfied: () => isFilled(DESCRIPTION),
      waitingHint: 'Write a note, or move on — this one is optional.',
    },

    {
      id: 'campaign-predictions',
      label: 'Forecast',
      title: 'Ask for a forecast first',
      description:
        'Switched on for you. With a template and an audience both in place, this estimates how much of the send will land, be read, and be blocked, from how comparable wording has performed — a sanity check before you commit the spend, not a promise. It stays greyed out until both are chosen, which is why it comes last rather than beside the template.',
      anchor: { selector: PREDICTIONS },
      side: 'left',
      spotlight: true,
      // The checkbox exists (disabled) from the moment the drawer opens, so it is
      // already there to be ringed — but it is unchecked by default, and a Back into
      // this step finds it checked, which would otherwise skip the highlight.
      forceHighlight: true,
      prepare: async () => {
        if (isChecked(PREDICTIONS)) return;
        ensureChecked(PREDICTIONS);
        await waitUntil(() => isChecked(PREDICTIONS), 1500);
      },
      satisfied: () => isChecked(PREDICTIONS),
      waitingHint: 'Turning the forecast on…',
    },

    {
      id: 'campaign-send-mode',
      label: 'Timing',
      title: 'Send now, or schedule it',
      description:
        'The button says what will happen: Send Now. The chevron beside it turns this into a scheduled send instead — pick a date, a time and a timezone, and the campaign sits until then, editable and cancellable the whole time. Leaving it on Send Now.',
      anchor: SEND_CONTROL,
      side: 'top',
      spotlight: true,
      // Nothing to click: immediate is the drawer's own mount default. The step
      // exists to show where scheduling lives, so it must always draw its ring.
      forceHighlight: true,
      // 🔴 DELIBERATELY NO `satisfied`. There is nothing for the user to do here, so
      // any predicate would be describing some OTHER step's state — an earlier
      // version asserted the audience selection and would have recorded this step as
      // skipped whenever that was cleared, for a step that cannot be failed.
    },

    {
      id: 'campaign-schedule-options',
      label: 'Schedule',
      title: 'Inside the send menu',
      description:
        'This is what the chevron opens. Send Now is ticked; choosing Schedule for later adds a date, a time and a timezone, and the campaign then sits in the list as SCHEDULED — editable and cancellable right up to the minute it goes out. We are leaving it on Send Now, and closing this again on the way past.',
      anchor: { selector: SEND_OPTIONS_POPOVER },
      side: 'left',
      spotlight: true,
      /**
       * 🔴 NO `forceHighlight`, and this is the clearest case for the rule. This
       * step's target IS the popover, and the only thing that opens the popover is
       * this step's own `prepare` — so forcing the pre-`prepare` wait would spend the
       * full five-second `RESOLVE_TIMEOUT_MS` looking for an element nothing has
       * created yet, on every single run, before finally opening it. Skipping that
       * wait costs nothing: `prepare` awaits the popover itself, and the `resolve()`
       * immediately after it draws the ring.
       */
      prepare: async () => {
        if (sendOptionsOpen()) return;
        await clickWhenReady(SEND_OPTIONS);
        await waitUntil(sendOptionsOpen, 2000);
      },
      /**
       * 🔴 CLOSES WHAT IT OPENED. The popover sits directly over the send button the
       * next step points at, and it does NOT dismiss itself when the callout is
       * clicked — `modalSafeChromeProps` deliberately keeps the flow's own chrome
       * from reaching Radix's document-level dismiss listener, so a popover opened by
       * a step stays open until that step puts it away.
       */
      onNext: closeSendOptions,
      /**
       * 🔴 DELIBERATELY NO `satisfied`, and it is not an oversight. The obvious
       * predicate — "is the popover open?" — is FALSE by the time it would be read:
       * `advanceFromNext` awaits `onNext` (which closes it) and only then checks,
       * so every run would record this step as SKIPPED for doing exactly what it is
       * supposed to do, and the completion panel would warn about it.
       */
    },

    {
      id: 'campaign-submit',
      label: 'Send',
      title: 'Create and send',
      description:
        'Everything required is in place: a name, an approved template and an audience — which is why the button is no longer greyed out. Cancel beside it throws the whole draft away; this one creates the campaign, and because the mode is immediate, creating it also starts the send. Nothing leaves this browser: the campaign and its delivery numbers are written to your demo data, and it appears as the first card on the board behind this drawer.',
      anchor: CAMPAIGN_SUBMIT,
      side: 'top',
      spotlight: true,
      advance: 'auto',
      /**
       * Puts the send menu away if the step before it is still holding it open —
       * the popover is anchored to the chevron and sits directly over the button
       * this step is pointing at. Idempotent: a no-op when it is already closed.
       */
      prepare: closeSendOptions,
      // The drawer closes itself on success; if the form refuses, it stays open
      // with its own message and this step keeps waiting.
      satisfied: () => !campaignFormOpen(),
      waitingHint: 'Waiting for you to press the send button…',
      optional: false,
    },

    /* ---------------- Tab two: Templates ---------------- */

    {
      id: 'templates-tab',
      label: 'Templates',
      title: 'Templates — the wording behind every send',
      description:
        'The campaign you just built had to pick its wording from a list of approved templates, and this tab is where that list comes from. Meta requires a template to be approved BEFORE a campaign can use it, because a campaign is what starts the conversation — so this tab is the first stop for anything you have not sent before.',
      anchor: TEMPLATES_TAB,
      side: 'bottom',
      spotlight: true,
      ready: onCampaignsTab,
      /**
       * The click lives in `onNext`, not `prepare`: `prepare` runs the instant the
       * step is ENTERED, so the tab would switch before the user had seen the ring
       * that explains why. Their own click on the real tab works too — the guard
       * makes the follow-up press a no-op.
       */
      prepare: async () => {},
      onNext: async () => {
        if (onTemplatesTab()) return;
        await clickWhenReady(TEMPLATES_TAB);
        await waitUntil(onTemplatesTab, 5000);
      },
      satisfied: onTemplatesTab,
      nextLabel: 'Try it: write a template',
      /**
       * The LAST segment, so its way out is the flow's way out: leave the
       * walkthrough and carry the tour on to the next module, rather than landing
       * back on the stop the user just chose to move past.
       */
      skipTo: 'end',
      waitingHint: 'Press the button to open the Templates tab.',
    },

    {
      id: 'template-open',
      label: 'New template',
      title: 'Write a template',
      description:
        'Every card here is one piece of wording, with the status Meta gave it. A template is a message with blanks in it, submitted once and then reusable for every send — which is why the campaign form could offer you a list to pick from rather than a text box to write in.',
      anchor: NEW_TEMPLATE,
      side: 'bottom',
      spotlight: true,
      ready: onTemplatesTab,
      prepare: async () => {},
      onNext: async () => {
        if (templateDrawerOpen()) return;
        await clickWhenReady(NEW_TEMPLATE);
      },
      satisfied: templateDrawerOpen,
      /**
       * Back over this step closes what its Next opened — see `TaskStep.rewind`.
       *
       * Without it, Back from the step after this one landed HERE with the
       * template composer still covering the New Template
       * button this step is about, which `resolve()` then refuses to ring: a step on screen with nothing
       * highlighted, which is what "Back disturbs the tour" looks like.
       */
      rewind: async () => {
        await closeFlowChrome(templateDrawerOpen, 'rewind');
      },
      nextLabel: 'Open the composer',
      waitingHint: 'Press the button to open the template composer.',
    },

    {
      id: 'template-ai-option',
      label: 'By hand',
      title: 'Draft it with AI, or write it yourself',
      description:
        'The composer opens on the empty form, with AI as an opt-in: press Generate with AI, describe the message in a sentence, and it writes three versions for you to pick from and then edit like any other. We are filling this one in by hand instead, so you can see what a template is actually made of — the button stays there for the next one.',
      anchor: { selector: AI_OPEN },
      side: 'left',
      spotlight: true,
      /**
       * 🔴 MANUAL, NOT `auto`, AND THAT IS THE FIX. Its predecessor was an `auto`
       * step whose `satisfied()` — "is the hand-filled form on screen?" — is true the
       * instant the drawer opens, so `TaskEngine.enter`'s already-satisfied fast path
       * skipped it on every single run and the AI route was never explained at all.
       * A manual step is never silently skipped: the fast path only ever short-cuts
       * an `auto` one.
       */
      prepare: async () => {
        if (!aiPanelOpen()) return;
        await clickWhenReady(AI_HIDE);
        await waitUntil(templateFormVisible, 2000);
      },
      satisfied: templateFormVisible,
      waitingHint: 'Opening the form…',
    },

    {
      id: 'template-name',
      label: 'Name',
      title: 'Name the template',
      description:
        'Filled in for you. Meta is strict here: lowercase letters, numbers and underscores only, no spaces — the composer lowercases and swaps spaces for underscores as you type rather than refusing them. The name has to be unique in your account, which is why this one carries the time, and it is an internal identifier the customer never sees.',
      anchor: { selector: TEMPLATE_NAME },
      side: 'right',
      spotlight: true,
      fill: [[TEMPLATE_NAME, templateNameValue]],
      satisfied: () => isFilled(TEMPLATE_NAME),
      waitingHint: 'Give the template a name to carry on.',
      optional: false,
    },

    {
      id: 'template-category',
      label: 'Category',
      title: 'Say what kind of message this is',
      description:
        'Left on Marketing, which is what a festive offer is. These three are not a filing system — Meta prices them differently and holds them to different rules. Marketing is anything promotional and costs the most. Utility follows something the customer already did, an order or a booking, and costs less. Authentication is reserved for one-time codes and cannot be sent by a campaign at all. Choosing the wrong one is the commonest reason a template comes back rejected.',
      anchor: TEMPLATE_CATEGORY,
      side: 'bottom',
      spotlight: true,
      // Ringed and explained, never changed — see `basicField` for why none of the
      // three dropdowns on this row is automated.
    },

    {
      id: 'template-layout',
      label: 'Layout',
      title: 'One message, or a swipeable set',
      description:
        'Left on Standard: a single message with a header, a body, a footer and buttons — everything the rest of this form asks for. Carousel is two to ten swipeable cards instead, each with its own image, wording and buttons. Meta allows it for Marketing only, which is why this choice disappears the moment the category above changes, and why the form below it changes shape entirely when you pick it.',
      anchor: TEMPLATE_LAYOUT,
      side: 'bottom',
      spotlight: true,
    },

    {
      id: 'template-language',
      label: 'Language',
      title: 'The language you are writing in',
      description:
        'Left on English. A template is approved PER LANGUAGE, so the same wording in Hindi is a second submission carrying the same name — which is how one campaign can reach each contact in the language they read. Declare it honestly: the review reads your text against the language you claimed.',
      anchor: TEMPLATE_LANGUAGE,
      side: 'bottom',
      spotlight: true,
    },

    {
      id: 'template-header',
      label: 'Header',
      title: 'Add a header',
      description:
        'Optional, and switched on for you as a text header. One header per template, in one of five formats: a line of text, an image, a video, a document, or a location — and None, at the start of the row, switches it back off. The media formats need a real file uploaded before Meta will accept the template; text needs nothing but the line itself, which is the next step.',
      anchor: HEADER_CHIPS,
      side: 'right',
      spotlight: true,
      prepare: enableTextHeader,
      satisfied: textHeaderOn,
      waitingHint: 'Switching the text header on…',
    },

    {
      id: 'template-header-text',
      label: 'Header text',
      title: 'Write the header',
      description:
        'Filled in for you. Sixty characters on one line, rendered in bold above the message — a subject line rather than a sentence, and it may carry at most one blank. Turning the header on is what made this field REQUIRED: Meta rejects a template whose header is enabled and empty, and the composer keeps Create Template greyed out until it has something.',
      anchor: { selector: HEADER_TEXT },
      side: 'right',
      spotlight: true,
      // Re-asserts its own precondition: the field exists only while the header is
      // on, so pressing Back from a later step — or turning the header off by hand —
      // would otherwise leave this pointing at nothing. No `forceHighlight`, for the
      // reason the campaign flow's advanced-settings steps give.
      prepare: enableTextHeader,
      fill: [[HEADER_TEXT, TEMPLATE_HEADER_VALUE]],
      satisfied: () => isFilled(HEADER_TEXT),
      waitingHint: 'Give the header a line to carry on.',
    },

    {
      id: 'template-body',
      label: 'Message',
      title: 'Write the message',
      description:
        'Filled in for you, and this is the part the customer reads. Curly braces make a blank — {{1}} — that each campaign fills in per recipient, which is what makes one approved template serve every send. Number them from one, in order, and never mix numbered blanks with named ones in the same body. Meta rejects a body that begins or ends with a blank, so keep words on both sides of every one.',
      anchor: { selector: TEMPLATE_BODY },
      side: 'right',
      spotlight: true,
      fill: [[TEMPLATE_BODY, TEMPLATE_BODY_VALUE]],
      satisfied: () => isFilled(TEMPLATE_BODY),
      waitingHint: 'Write a message to carry on.',
      optional: false,
    },

    {
      id: 'template-samples',
      label: 'Samples',
      title: 'Show Meta what the blanks hold',
      description:
        'Filled in for you, and this panel appeared the moment the body gained its blanks. Meta reviews the message with your samples substituted in, so an example is what turns two curly braces into something a human can judge. Keep them realistic and keep them fictional — the panel says so itself, because a real customer name or number in here is a privacy problem in someone else’s review queue.',
      anchor: SAMPLES_PANEL,
      side: 'right',
      spotlight: true,
      fill: [
        [variableSample(1), TEMPLATE_SAMPLE_VALUES[0]],
        [variableSample(2), TEMPLATE_SAMPLE_VALUES[1]],
      ],
      satisfied: allSamplesFilled,
      waitingHint: 'Give every blank a sample to carry on.',
    },

    {
      id: 'template-footer',
      label: 'Footer',
      title: 'The opt-out line',
      description:
        'Already filled in, and left exactly as it is: the composer defaults every marketing template to "Reply STOP to unsubscribe". Sixty characters, no blanks allowed, rendered small and grey under the message. Meta does not force an opt-out line — but a marketing template without one collects blocks, and blocks are what pull your number’s quality rating down.',
      anchor: { selector: TEMPLATE_FOOTER },
      side: 'right',
      spotlight: true,
      /**
       * 🔴 NO `fill` AND NO `satisfied`. The composer already put the line there, and
       * `fillField` leaves a non-empty field alone by design — declaring a fill would
       * be a no-op that reads as though the flow typed it. A predicate would be worse
       * than useless: the footer is genuinely optional, so a user who clears it has
       * done nothing wrong and the bar must not record the step as skipped for it.
       */
    },

    {
      id: 'template-buttons',
      label: 'Buttons',
      title: 'Add a button',
      description:
        'Optional, and up to ten per template: quick replies, which send their own words back to you when tapped; up to two URL buttons; one phone button; and one that opens a WhatsApp Flow. We will add a quick reply — the tap that replaces "Reply SHOP" in the message with something the customer does not have to type.',
      anchor: ADD_BUTTON,
      side: 'right',
      spotlight: true,
      /**
       * The press lives in `onNext` so the row appears when the user asks for it,
       * rather than on entry behind the callout explaining what it does — the same
       * split the campaign flow's advanced-settings step makes.
       */
      onNext: async () => {
        if (quickReplyRowAdded()) return;
        await clickWhenReady(ADD_BUTTON);
        await waitUntil(quickReplyRowAdded, 2000);
      },
      satisfied: quickReplyRowAdded,
      nextLabel: 'Add a quick reply',
      waitingHint: 'Press the button to add one.',
    },

    {
      id: 'template-button-text',
      label: 'Button text',
      title: 'Label the button',
      description:
        'Filled in for you, and required — an empty button row is what keeps Create Template greyed out, so adding a button and leaving it blank is worse than not adding one. Forty characters. On a quick reply the label is both what the customer taps AND what arrives back in your inbox, so make it something you can act on. The dropdown beside it turns this row into a URL, phone or Flow button, each of which asks for one more field.',
      anchor: { selector: QUICK_REPLY_TEXT },
      side: 'right',
      spotlight: true,
      fill: [[QUICK_REPLY_TEXT, TEMPLATE_BUTTON_VALUE]],
      satisfied: () => isFilled(QUICK_REPLY_TEXT),
      waitingHint: 'Give the button a label to carry on.',
    },

    {
      id: 'template-preview',
      label: 'Preview',
      title: 'Read it back as the customer will',
      description:
        'Everything you have entered, stacked the way WhatsApp stacks it: the bold header, the body with your samples standing in for the blanks, the grey footer, and the button under all of it. Worth reading before every submission — this is the arrangement Meta reviews, not the list of boxes on the left.',
      anchor: { selector: PREVIEW },
      side: 'left',
      spotlight: true,
    },

    {
      id: 'template-submit',
      label: 'Create',
      title: 'Submit it for approval',
      description:
        'Everything Meta requires is in place: a name, a category, a language, a body, and a line for the header and the button we switched on. Cancel beside it throws the draft away. Creating it SUBMITS it, which is why it lands in the list as PENDING rather than ready to use — real approval takes anywhere from minutes to a day, and only then would it appear in the campaign form’s picker, which lists approved templates only. Nothing leaves this browser: the template is written to your demo data.',
      anchor: TEMPLATE_SUBMIT,
      side: 'top',
      spotlight: true,
      /**
       * 🔴 THE USER PRESSES THIS, exactly like the campaign send above — it is NOT
       * clicked from `onNext`. The drawer closes itself only on success, so an
       * assisted click that then advanced regardless would finish the flow with the
       * drawer still open and its validation message unread (a duplicate name is the
       * realistic case). Waiting here keeps the failure where the user can see it,
       * and `advance: 'auto'` means the flow completes the instant the record is
       * genuinely created.
       */
      advance: 'auto',
      satisfied: () => !templateDrawerOpen(),
      waitingHint: 'Press Create Template to submit it…',
      optional: false,
    },
  ],
};
